/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */

import { isTokenExpired } from './isTokenExpired'

export const WEBVIEW_SUCCESS_CALLBACK = 'messageToWebView'

export type EmbeddedAction =
  | { action: 'getAuthData'; body: { successCallback: string } }
  | { action: 'getRefreshAuthData'; body: { successCallback: string } }
  | { action: 'authResult'; body: { valid: boolean } }
  | { action: 'goBack' }
  | { action: 'log'; body: { message: string } }

export type FromNativePayload = {
  action: 'provideToken'
  token: string
}

interface CustomBridge {
  webkit: {
    messageHandlers: {
      bridge: {
        postMessage: (payload: EmbeddedAction) => void
      }
    }
  }
  [WEBVIEW_SUCCESS_CALLBACK]: (payload: FromNativePayload) => void
  nativeAppHeyTradeApi: {
    bridge: (payload: EmbeddedAction) => void
  }
}

/**
 * Global type declarations
 */
declare global {
  interface Window extends CustomBridge {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    analytics: any
  }
}

export type ManagerState = 'PAUSED' | 'READY'

/* eslint-disable no-console */
export interface IAppManager {
  /**
   * Defines the web mode, it can be "jsbridge" or "regular"
   */
  type: WebMode
  authenticate: (cb: (data: { token: string }) => void) => void
  logger: (msg: string) => void
  clean: () => void
  token: string | null | undefined
  embedded: boolean
  goBack: () => void
  sendMessage: (msg: EmbeddedAction) => void
  id: string
  state: ManagerState
  refreshTokenSecondsOffset?: number
}

export type WebMode = 'jsbridge' | 'normal'

export class JSBridgeAppManager implements IAppManager {
  type: WebMode = 'jsbridge'
  token: string = null
  debug: boolean
  embedded = true
  id: string = ''
  state: ManagerState = 'READY'
  refreshTokenSecondsOffset = 15000

  constructor({ debug }: { debug?: boolean }) {
    this.debug = debug
    this.id = Math.random().toString(36).substring(2, 8)
    this.logger('Initializing JSBridgeAppManager...')

    // Add mode to html
    document?.querySelector('html')?.classList?.add(`--mode-${this.type}`)
  }

  logger(msg: string): void {
    if (this.debug) {
      const id = 'Webview'
      console.log(`${this.id} - [${id}] - ${msg}`)
      this.sendMessage({
        action: 'log',
        body: {
          message: `[Webview/${
            this.id
          }] - ${new Date()?.toISOString()} - ${msg}`,
        },
      })
    }
  }

  clean(): void {
    this.token = null
  }

  authenticate(cb: (data: { token: string }) => void): void {
    if (this.state === 'PAUSED') return
    this.state = 'PAUSED'

    // Set function
    window[WEBVIEW_SUCCESS_CALLBACK] = (payload) => {
      if (payload?.action === 'provideToken') {
        this.logger(`Checking if token is valid`)
        const isValid = isTokenExpired(
          payload?.token,
          this.refreshTokenSecondsOffset,
        )
        if (isValid?.valid) {
          this.logger(`Token is valid`)
          this.sendMessage({
            action: 'authResult',
            body: {
              valid: true,
            },
          })
          this.token = payload?.token
          cb({ token: payload?.token })
          return
        } else {
          this.sendMessage({
            action: 'authResult',
            body: {
              valid: false,
            },
          })
          this.logger('Invalid token')
        }
      }
      this.state = 'READY'
    }

    // Check if current token is valid before requesting a new one
    const cachedTokenIsValid = isTokenExpired(
      this.token,
      this.refreshTokenSecondsOffset,
    )
    console.log('cachedTokenIsValid', cachedTokenIsValid)
    if (this.token && cachedTokenIsValid?.valid) {
      this.logger(`Cached token found, no need to request a new one.`)
      cb({ token: this.token })
    } else {
      // Request new token
      this.logger(`Requesting new token from WebView...`)
      this.sendMessage({
        action: this?.token?.length ? 'getRefreshAuthData' : 'getAuthData',
        body: {
          successCallback: WEBVIEW_SUCCESS_CALLBACK,
        },
      })
    }

    this.state = 'READY'
  }

  goBack(): void {
    this.sendMessage({
      action: 'goBack',
    })
  }

  sendMessage(message: EmbeddedAction): void {
    if (
      window?.nativeAppHeyTradeApi &&
      typeof window.nativeAppHeyTradeApi.bridge === 'function'
    ) {
      window?.nativeAppHeyTradeApi.bridge(
        JSON.stringify(message) as unknown as EmbeddedAction,
      )
    } else if (
      window?.webkit &&
      window?.webkit.messageHandlers &&
      window?.webkit.messageHandlers.bridge
    ) {
      window?.webkit?.messageHandlers?.bridge?.postMessage(
        JSON.stringify(message) as unknown as EmbeddedAction,
      )
    } else {
      console.log('No native bridge available')
    }
  }
}

export const createAppManager = (
  type: WebMode,
  debug?: boolean,
): IAppManager | null => {
  if (type === 'jsbridge') {
    return new JSBridgeAppManager({ debug })
  }

  return null
}
